import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import { JobPhaseReceivableSheetInterface } from '@vatos-pas/common';

import { createRowRegistry } from './createRowRegistry';

type ReceivableRegistryValue = {
  /** Reactive read: the id the API returned this session for a row, or null. */
  getId: (jobPhaseId: string) => string | null;
  /** Remember the id a save returned so sibling cells see it without a refetch. */
  setId: (jobPhaseId: string, id: string) => void;
};

const {
  Context: ReceivableRegistryContext,
  useRegistry: useReceivableRegistry,
} = createRowRegistry<ReceivableRegistryValue>(
  'useReceivableRegistry',
  'ReceivableRegistryProvider',
);

export { useReceivableRegistry };

/**
 * Row-scoped receivable-id cache, provided once around the receivables list.
 *
 * Writes go through an idempotent PUT keyed by the phase, so the registry no
 * longer has to guarantee "exactly one create per row" (the API does). It
 * only remembers the id each save returned, so the history cell of a row
 * created this session can open without a list refetch.
 * `record.jobPhaseReceivableId` always takes precedence, so a natural list
 * refetch transparently supersedes the session-local id.
 */
export const ReceivableRegistryProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [ids, setIds] = useState<Record<string, string>>({});

  const setId = useCallback((jobPhaseId: string, id: string) => {
    setIds(prev => (prev[jobPhaseId] === id ? prev : { ...prev, [jobPhaseId]: id }));
  }, []);

  const value = useMemo<ReceivableRegistryValue>(
    () => ({
      getId: (jobPhaseId: string) => ids[jobPhaseId] ?? null,
      setId,
    }),
    [ids, setId],
  );

  return (
    <ReceivableRegistryContext.Provider value={value}>
      {children}
    </ReceivableRegistryContext.Provider>
  );
};

/**
 * Effective receivable id for a row: the record's own id, else the id the API
 * returned this session (reactive — a sibling's save propagates without a
 * refetch). `record.jobPhaseReceivableId` always wins. The single source of
 * the id rule, shared by useOptimisticReceivable (write path) and the
 * read-only history cell (which needs only the id, not the commit stack).
 */
export const useReceivableId = (
  record: JobPhaseReceivableSheetInterface | undefined,
): string | null => {
  const registry = useReceivableRegistry();
  const jobPhaseId = record?.jobPhaseId;
  return (
    record?.jobPhaseReceivableId ??
    (jobPhaseId ? registry.getId(jobPhaseId) : null)
  );
};
